import type { DependencyList } from 'react';
import { useRef } from 'react';

/**
 * Implementation source: https://github.com/alibaba/hooks/blob/v3.1.13/packages/hooks/src/utils/depsAreSame.ts
 */
function depsAreSame(oldDeps: DependencyList, deps: DependencyList): boolean {
  if (oldDeps === deps) return true;
  return oldDeps.every((oldDep, index) => Object.is(oldDep, deps[index]));
}

/**
 * useCreation is the replacement for useMemo or useRef.
 * useMemo can't guarantee the memoized value will not be recalculated
 * as the the official document of React.js says that.
 * Doc: https://ahooks.js.org/hooks/use-creation
 * Implementation source: https://github.com/alibaba/hooks/blob/v3.1.13/packages/hooks/src/useCreation/index.ts
 */
export function useCreation<T>(factory: () => T, deps: DependencyList): T {
  const { current } = useRef({
    deps,
    obj: undefined as undefined | T,
    initialized: false,
  });
  if (current.initialized === false || !depsAreSame(current.deps, deps)) {
    current.deps = deps;
    current.obj = factory();
    current.initialized = true;
  }
  return current.obj as T;
}
